<script>
	import { createEventDispatcher } from "svelte";
	import { navigate } from "svelte-routing";
	import { Button, DropdownItem, Table } from "@sveltestrap/sveltestrap";
	import CustomTableStatusDropdown from "../../../components/CustomTable/CustomTableStatusDropdown.svelte";
	import TableDropdown from "../../../components/TableDropdown/TableDropdown.svelte";
	import { convert } from "src/services/helpers";
	import { issueStatusOptions } from "src/utils/options";
	import { isMnrPhase2Enabled } from "src/config/flag-config";

	export let issues = [];
	export let readOnly = false;
	export let isWshop = false;
	export let isSelectingForCompletion = false;
	export let selectedIssueIds = [];
	export let showCompletionOutcome = false;

	function toggleIssueSelected(issueId) {
		selectedIssueIds = selectedIssueIds.includes(issueId)
			? selectedIssueIds.filter((id) => id !== issueId)
			: [...selectedIssueIds, issueId];
	}

	function toggleSelectAll() {
		selectedIssueIds = allSelected
			? []
			: sortedIssues.map((issue) => issue.id);
	}

	const dispatch = createEventDispatcher();

	const issueStatusLabelByValue = Object.fromEntries(
		issueStatusOptions.map((option) => [option.value, option.label])
	);
	const issueStatusVariantByLabel = Object.fromEntries(
		issueStatusOptions.map((option) => [option.label, option.variant])
	);

	function toIssueStatusDisplayRow(issue) {
		return {
			...issue,
			status: issueStatusLabelByValue[issue.status] || issue.status,
			allowedTransitions: readOnly
				? []
				: (issue.allowedTransitions || []).map(
						(transition) =>
							issueStatusLabelByValue[transition] || transition
					),
		};
	}

	let sortDirection = "descending";

	function toggleReportedAtSort() {
		sortDirection =
			sortDirection === "ascending" ? "descending" : "ascending";
	}

	$: sortedIssues = [...issues].sort((a, b) => {
		const result =
			new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
		return sortDirection === "ascending" ? result : -result;
	});

	$: allSelected =
		sortedIssues.length > 0 &&
		sortedIssues.every((issue) => selectedIssueIds.includes(issue.id));
</script>

<div class="d-flex align-items-center mb-3">
	<h5 class="fw-bold mb-0">Issues</h5>
	{#if !isWshop}
		<Button
			class="themeButton backButton ms-auto px-5"
			disabled={readOnly}
			title={readOnly
				? "This work order is cancelled or completed, so issues can no longer be added to it."
				: undefined}
			on:click={() => dispatch("addIssue")}
		>
			Add issue
		</Button>
	{/if}
</div>
{#if isSelectingForCompletion}
	<div class="select-mode-banner mb-3">
		<img
			src="/assets/icon-circle-info.svg"
			alt=""
			class="select-mode-banner-icon"
		/>
		<span>Select issue(s) that have been fixed</span>
	</div>
{/if}
{#if isWshop}
	<Table responsive hover>
		<thead>
			<tr>
				<th>
					{#if isSelectingForCompletion}
						<div class="form-check">
							<input
								class="form-check-input"
								type="checkbox"
								id="select-all-issues"
								checked={allSelected}
								on:change={toggleSelectAll}
							/>
							<label
								class="form-check-label"
								for="select-all-issues"
							>
								ID
							</label>
						</div>
					{:else}
						ID
					{/if}
				</th>
				<th>Service category</th>
				<th>Issue type</th>
				<th>Issue description</th>
				<th
					aria-sort={sortDirection === "ascending"
						? "ascending"
						: "descending"}
				>
					<button
						type="button"
						class="sort-toggle d-flex justify-content-between align-items-center w-100"
						on:click={toggleReportedAtSort}
					>
						Date & time reported
						{#if sortDirection === "ascending"}
							<img src="/assets/icon-sort-down-thin.svg" alt="" />
						{:else}
							<img src="/assets/icon-sort-up-thin.svg" alt="" />
						{/if}
					</button>
				</th>
			</tr>
		</thead>
		<tbody>
			{#if sortedIssues.length === 0}
				<tr>
					<td colspan="5">
						<b class="d-flex justify-content-center">
							No issues for this work order
						</b>
					</td>
				</tr>
			{:else}
				{#each sortedIssues as issue}
					<tr
						class:selectable-row={isSelectingForCompletion}
						on:click={() =>
							isSelectingForCompletion &&
							toggleIssueSelected(issue.id)}
					>
						<td>
							{#if isSelectingForCompletion}
								<div
									class="form-check"
									on:click|stopPropagation
								>
									<input
										class="form-check-input"
										type="checkbox"
										id={`select-issue-${issue.id}`}
										checked={selectedIssueIds.includes(
											issue.id
										)}
										on:change={() =>
											toggleIssueSelected(issue.id)}
									/>
									<label
										class="form-check-label"
										for={`select-issue-${issue.id}`}
									>
										{issue.code}
									</label>
								</div>
							{:else if showCompletionOutcome}
								<div class="d-flex align-items-center gap-2">
									<label
										class="visually-hidden"
										for={`issue-outcome-${issue.id}`}
									>
										{issue.code} outcome
									</label>
									<input
										class="form-check-input mt-0"
										type="checkbox"
										id={`issue-outcome-${issue.id}`}
										checked={issue.outcome === "Fixed"}
										disabled
									/>
									<button
										type="button"
										class="link link-text issue-code-link"
										on:click={() =>
											dispatch("openDetail", issue)}
									>
										{issue.code}
									</button>
								</div>
							{:else}
								<button
									type="button"
									class="link link-text issue-code-link"
									on:click={() =>
										dispatch("openDetail", issue)}
								>
									{issue.code}
								</button>
							{/if}
						</td>
						<td>{issue.serviceCategory}</td>
						<td>{issue.issueType?.name || "-"}</td>
						<td
							class="issue-description"
							title={issue.issueDescription}
							>{issue.issueDescription}</td
						>
						<td>{convert(issue.createdAt)}</td>
					</tr>
				{/each}
			{/if}
		</tbody>
	</Table>
{:else}
	<Table responsive hover>
		<thead>
			<tr>
				<th>ID</th>
				<th>Service category</th>
				<th>Issue type</th>
				<th>Issue description</th>
				<th>Issue status</th>
				<th
					aria-sort={sortDirection === "ascending"
						? "ascending"
						: "descending"}
				>
					<button
						type="button"
						class="sort-toggle d-flex justify-content-between align-items-center w-100"
						on:click={toggleReportedAtSort}
					>
						Date & time reported
						{#if sortDirection === "ascending"}
							<img src="/assets/icon-sort-down-thin.svg" alt="" />
						{:else}
							<img src="/assets/icon-sort-up-thin.svg" alt="" />
						{/if}
					</button>
				</th>
				<th>Zendesk ID</th>
				<th>Related case</th>
				<th>Actions</th>
			</tr>
		</thead>
		<tbody>
			{#if issues.length === 0}
				<tr>
					<td colspan="9">
						<b class="d-flex justify-content-center">
							No issues for this work order
						</b>
					</td>
				</tr>
			{:else}
				{#each sortedIssues as issue}
					<tr>
						<td>
							<button
								type="button"
								class="link link-text issue-code-link"
								on:click={() => dispatch("openDetail", issue)}
							>
								{issue.code}
								{#if isMnrPhase2Enabled && issue.handoverFlagged}
									<img
										src="/assets/icon-alert-filled-red.svg"
										alt="Flagged"
										class="flag-icon"
									/>
								{/if}
							</button>
						</td>
						<td>{issue.serviceCategory}</td>
						<td>{issue.issueType?.name || "-"}</td>
						<td
							class="issue-description"
							title={issue.issueDescription}
							>{issue.issueDescription}</td
						>
						<td>
							<CustomTableStatusDropdown
								row={toIssueStatusDisplayRow(issue)}
								key="status"
								options={issueStatusVariantByLabel}
								event={(_row, statusLabel) =>
									dispatch("statusSelect", {
										issue,
										statusLabel,
									})}
							/>
						</td>
						<td>{convert(issue.createdAt)}</td>
						<td>
							{#if issue.zendeskId}
								<a
									href={`https://getgosupport.zendesk.com/agent/tickets/${issue.zendeskId}`}
									target="_blank"
									rel="noopener noreferrer"
									class="link link-text zendesk-link"
								>
									{issue.zendeskId}
								</a>
							{:else}
								-
							{/if}
						</td>
						<td>
							{#if issue.relatedCaseNo}
								<button
									type="button"
									class="link link-text related-case-link"
									on:click={() =>
										navigate(
											`/issues/case-management?tab=&caseNo=${issue.relatedCaseNo}`
										)}
								>
									{issue.relatedCaseNo}
								</button>
							{:else}
								-
							{/if}
						</td>
						<td>
							{#if !readOnly}
								<TableDropdown>
									<DropdownItem
										on:click={() =>
											dispatch(
												"moveToAnotherWorkOrder",
												issue
											)}
									>
										Move issue to another work order
									</DropdownItem>
									<DropdownItem
										on:click={() =>
											dispatch("moveToBacklog", issue)}
									>
										Move issue to backlog
									</DropdownItem>
								</TableDropdown>
							{/if}
						</td>
					</tr>
				{/each}
			{/if}
		</tbody>
	</Table>
{/if}

<style>
	.issue-description {
		max-width: 280px;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	.issue-code-link {
		white-space: nowrap;
	}

	.flag-icon {
		width: 24px;
		height: 24px;
		margin-left: 4px;
		vertical-align: -0.125em;
	}

	.issue-code-link,
	.related-case-link,
	.zendesk-link {
		border: none;
		background: none;
		padding: 0;
	}

	.selectable-row {
		cursor: pointer;
	}

	/* .backButton's white background otherwise wins the specificity tie
	   against App.css's shared :disabled rule. */
	:global(.btn.themeButton.backButton:disabled) {
		background-color: var(--fds-surface-container-high);
		color: var(--fds-on-surface-container-variant);
		border-color: transparent;
	}

	.sort-toggle {
		background: none;
		border: none;
		padding: 0;
		cursor: pointer;
		font: inherit;
		color: inherit;
		text-align: left;
	}

	.select-mode-banner {
		display: flex;
		align-items: center;
		gap: 16px;
		background-color: var(--fds-primary-container, #e6ebfd);
		color: var(--fds-on-primary-container, #00071f);
		border-radius: 8px;
		padding: 16px;
		font-weight: bold;
	}

	.select-mode-banner-icon {
		width: 24px;
		height: 24px;
		flex-shrink: 0;
	}
</style>
